/**
 * OceanRadar — SVG radar chart overlaying:
 *   - the founder's OCEAN scores (gold, solid)
 *   - the founder's industry-average OCEAN (rose, dashed)
 *   - the full cohort-average OCEAN (slate, dotted)
 *
 * Pure SVG — no chart library. Five axes (O/C/E/A/N), 0-100 scale.
 * Interactive: hover a point to see the exact value + label.
 *
 * Props:
 *   founder:        { openness, conscientiousness, extraversion, agreeableness, neuroticism }
 *   industryAvg:    same shape OR null (industry not yet recognised)
 *   industryLabel:  string for legend (e.g. "FinTech avg")
 *   cohortAvg:      same shape OR null
 *   size:           pixel size of the square SVG (default 360)
 */
import React, { useState } from 'react';

const DIMS = [
  { key: 'openness',          label: 'Openness' },
  { key: 'conscientiousness', label: 'Conscientiousness' },
  { key: 'extraversion',      label: 'Extraversion' },
  { key: 'agreeableness',     label: 'Agreeableness' },
  { key: 'neuroticism',       label: 'Neuroticism' },
];

// Helpers — convert a polar (angle, radius) to cartesian relative to centre.
function pt(angleDeg, radius, cx, cy) {
  const r = (angleDeg - 90) * (Math.PI / 180); // start at top
  return [cx + radius * Math.cos(r), cy + radius * Math.sin(r)];
}

function buildPath(values, cx, cy, maxR) {
  // values: array of 0..100 matching DIMS order
  const angleStep = 360 / DIMS.length;
  return values
    .map((v, i) => {
      const r = (Math.max(0, Math.min(100, v)) / 100) * maxR;
      const [x, y] = pt(i * angleStep, r, cx, cy);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ') + ' Z';
}

function pointsArray(values, cx, cy, maxR) {
  const angleStep = 360 / DIMS.length;
  return values.map((v, i) => {
    const r = (Math.max(0, Math.min(100, v)) / 100) * maxR;
    return pt(i * angleStep, r, cx, cy);
  });
}

function readVals(obj) {
  if (!obj) return null;
  return DIMS.map((d) => Number(obj[d.key] || 0));
}

export default function OceanRadar({
  founder, industryAvg, industryLabel = 'Industry avg',
  cohortAvg, size = 360,
}) {
  const [hover, setHover] = useState(null); // {i, x, y, val, label, layer}
  const cx = size / 2;
  const cy = size / 2;
  const padding = 56; // room for axis labels
  const maxR = (size - padding * 2) / 2;

  const fVals = readVals(founder);
  const iVals = readVals(industryAvg);
  const cVals = readVals(cohortAvg);

  if (!fVals) return null;

  const gridLevels = [25, 50, 75, 100];

  return (
    <div className="relative" data-testid="ocean-radar">
      <svg
        width={size} height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="overflow-visible"
      >
        {/* GRID — concentric pentagons */}
        {gridLevels.map((level) => {
          const r = (level / 100) * maxR;
          const pts = DIMS.map((_, i) => pt(i * (360 / DIMS.length), r, cx, cy));
          return (
            <polygon
              key={level}
              points={pts.map(([x, y]) => `${x},${y}`).join(' ')}
              fill="none"
              stroke="#E2E8F0"
              strokeDasharray={level === 100 ? '' : '3 3'}
              strokeWidth={level === 100 ? 1.5 : 1}
            />
          );
        })}

        {/* GRID — spokes */}
        {DIMS.map((d, i) => {
          const [x, y] = pt(i * (360 / DIMS.length), maxR, cx, cy);
          return (
            <line
              key={d.key}
              x1={cx} y1={cy} x2={x} y2={y}
              stroke="#E2E8F0" strokeWidth={1}
            />
          );
        })}

        {/* COHORT polygon — bottom layer */}
        {cVals && (
          <>
            <path
              d={buildPath(cVals, cx, cy, maxR)}
              fill="#64748B" fillOpacity={0.08}
              stroke="#94A3B8" strokeOpacity={0.7}
              strokeWidth={1.5}
              strokeDasharray="2 4"
            />
            {pointsArray(cVals, cx, cy, maxR).map(([x, y], i) => (
              <circle
                key={`c-${i}`} cx={x} cy={y} r={3}
                fill="#94A3B8"
                onMouseEnter={() => setHover({ i, x, y, val: cVals[i],
                  label: DIMS[i].label, layer: 'Cohort avg' })}
                onMouseLeave={() => setHover(null)}
              />
            ))}
          </>
        )}

        {/* INDUSTRY polygon — middle layer */}
        {iVals && (
          <>
            <path
              d={buildPath(iVals, cx, cy, maxR)}
              fill="#E11D48" fillOpacity={0.10}
              stroke="#E11D48" strokeOpacity={0.75}
              strokeWidth={1.5}
              strokeDasharray="6 4"
            />
            {pointsArray(iVals, cx, cy, maxR).map(([x, y], i) => (
              <circle
                key={`i-${i}`} cx={x} cy={y} r={3.5}
                fill="#E11D48"
                onMouseEnter={() => setHover({ i, x, y, val: iVals[i],
                  label: DIMS[i].label, layer: industryLabel })}
                onMouseLeave={() => setHover(null)}
              />
            ))}
          </>
        )}

        {/* FOUNDER polygon — top layer */}
        <path
          d={buildPath(fVals, cx, cy, maxR)}
          fill="#8B3FC5" fillOpacity={0.28}
          stroke="#5E1F8B" strokeWidth={2.5}
        />
        {pointsArray(fVals, cx, cy, maxR).map(([x, y], i) => (
          <circle
            key={`f-${i}`} cx={x} cy={y} r={5}
            fill="#8B3FC5" stroke="#5E1F8B" strokeWidth={1.5}
            data-testid={`radar-founder-point-${DIMS[i].key}`}
            onMouseEnter={() => setHover({ i, x, y, val: fVals[i],
              label: DIMS[i].label, layer: 'You' })}
            onMouseLeave={() => setHover(null)}
            className="cursor-pointer"
          />
        ))}

        {/* AXIS LABELS */}
        {DIMS.map((d, i) => {
          const [x, y] = pt(i * (360 / DIMS.length), maxR + 22, cx, cy);
          // Align text by quadrant
          const angle = i * (360 / DIMS.length);
          let anchor = 'middle';
          if (angle > 0 && angle < 180) anchor = angle === 90 ? 'start' : (angle < 90 ? 'start' : 'start');
          if (angle > 180 && angle < 360) anchor = 'end';
          return (
            <text
              key={d.key}
              x={x} y={y}
              textAnchor={anchor}
              dominantBaseline="middle"
              fontSize="10.5"
              fontWeight="700"
              fill="#0F172A"
              style={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}
            >
              {d.label}
            </text>
          );
        })}

        {/* GRID LEVEL LABELS — only the outer two */}
        {[50, 100].map((level) => {
          const [x, y] = pt(0, (level / 100) * maxR + 4, cx, cy);
          return (
            <text
              key={level}
              x={x + 4} y={y - 2}
              fontSize="8.5"
              fill="#94A3B8"
              fontFamily="monospace"
            >
              {level}
            </text>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hover && (
        <div
          className="absolute pointer-events-none ve-card px-3 py-2 text-xs shadow-lg"
          style={{
            left: hover.x + 12,
            top: hover.y + 12,
            transform: 'translate(0, 0)',
          }}
        >
          <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500">
            {hover.layer}
          </div>
          <div className="font-bold mt-0.5">{hover.label}</div>
          <div className="display text-lg text-[#8B3FC5]">{Math.round(hover.val)}</div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
        <div className="flex items-center gap-2" data-testid="radar-legend-you">
          <span className="inline-block h-3 w-6 rounded" style={{ background: '#8B3FC5', border: '2px solid #5E1F8B' }}/>
          <span className="font-bold uppercase tracking-[0.18em] text-[10px]">You</span>
        </div>
        {iVals && (
          <div className="flex items-center gap-2" data-testid="radar-legend-industry">
            <span className="inline-block h-3 w-6 rounded" style={{ background: 'transparent',
              borderTop: '2px dashed #E11D48', borderBottom: '2px dashed #E11D48' }}/>
            <span className="font-bold uppercase tracking-[0.18em] text-[10px] text-rose-700">
              {industryLabel}
            </span>
          </div>
        )}
        {cVals && (
          <div className="flex items-center gap-2" data-testid="radar-legend-cohort">
            <span className="inline-block h-3 w-6 rounded" style={{ background: 'transparent',
              borderTop: '2px dotted #94A3B8', borderBottom: '2px dotted #94A3B8' }}/>
            <span className="font-bold uppercase tracking-[0.18em] text-[10px] text-slate-500">
              Cohort avg
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
